<script setup lang="ts">
import { useSiteMap } from '@/composables/eos/site-map'
import { dateTimeFormatter } from '@/helpers/ep/formatters'
import type { IHostingClientFilters } from '@/types/eos/filters'
import type { IHostingClient } from '@/types/eos/SiteMap'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { tableColumnPercentageWithTwoDecimals } from '@/util/formatters'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Icon } from '@iconify/vue'
import { reactive } from 'vue'

const filters = reactive<IHostingClientFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[1],
  name: undefined,
})

const { getHostingClientsQuery } = useSiteMap()

const { isFetching, isError, isRefetching, data, error, refetch } = getHostingClientsQuery(filters)

const nameSearchSettings = {
  key: 'search',
  value: 'Search',
  type: 'search',
  placeholder: 'Name',
  style: 'width: 256px',
  onChange: (val: string) => {
    if (val == '') {
      filters.name = undefined
    } else {
      filters.name = val
    }
  },
}

const minerTypeLabels: { key: keyof IHostingClient; label: string }[] = [
  { key: 'total_registered_miners', label: 'Total registered' },
  { key: 'online_miners', label: 'Online' },
  { key: 'offline_miners', label: 'Offline' },
  { key: 'pending_miners', label: 'Pending' },
  { key: 'disabled_miners', label: 'Disabled' },
  { key: 'hashing_miners', label: 'Hashing' },
  { key: 'sleeping_miners', label: 'Sleeping' },
  { key: 'deployed_miners', label: 'Deployed' },
  { key: 'in_repair_miners', label: 'In repair' },
]
</script>

<template>
  <ViewLayout>
    <rl-error-result v-if="isError && !isRefetching" :error="error">
      <el-button @click="refetch()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else>
      <div class="flex gap-2">
        <TableSearchFilter :search-settings="nameSearchSettings" :disabled="isFetching" />
      </div>
      <el-table v-loading="isFetching" :data="data?.data" empty-text="No data" stripe>
        <el-table-column prop="name" label="Name" :min-width="120" />
        <el-table-column prop="miners_under_contract" :min-width="100">
          <template #header>
            <div class="flex items-center gap-1">
              <span>Miners Under Contract</span>
              <el-tooltip content="Amount of miners that are currently under contract with the client" placement="top">
                <Icon
                  icon="material-symbols:help-outline"
                  class="text-gray-400 hover:text-gray-600 cursor-help"
                  height="16"
                  width="16"
                />
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Miners" :min-width="320">
          <template #header>
            <div class="flex items-center gap-1">
              <span>Miners</span>
              <el-tooltip placement="top">
                <template #content>
                  <div><strong>Total registered:</strong> Miners registered in the system</div>
                  <div><strong>Online / Offline:</strong> Connection status</div>
                  <div><strong>Pending / Disabled:</strong> Provisioning or disabled state</div>
                  <div><strong>Hashing:</strong> Miners that registered a hashrate in the last hour</div>
                  <div><strong>Sleeping / Deployed / In repair:</strong> Operational state</div>
                </template>
                <Icon
                  icon="material-symbols:help-outline"
                  class="text-gray-400 hover:text-gray-600 cursor-help"
                  height="16"
                  width="16"
                />
              </el-tooltip>
            </div>
          </template>
          <template #default="scope">
            <div class="grid grid-cols-3 gap-y-1 gap-x-4 text-sm leading-6">
              <div v-for="item in minerTypeLabels" :key="item.key" class="flex gap-1.5 items-baseline">
                <span class="shrink-0 text-text-tertiary">{{ item.label }}:</span>
                <span class="tabular-nums" :class="{ 'text-text-tertiary': scope.row[item.key] == null }">
                  {{ scope.row[item.key] ?? '–' }}
                </span>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          prop="uptime_threshold_percentage"
          :min-width="120"
          :formatter="tableColumnPercentageWithTwoDecimals"
        >
          <template #header>
            <div class="flex items-center gap-1">
              <span>Uptime Threshold</span>
              <el-tooltip content="Minimum uptime percentage required for the client" placement="top">
                <Icon
                  icon="material-symbols:help-outline"
                  class="text-gray-400 hover:text-gray-600 cursor-help"
                  height="16"
                  width="16"
                />
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Foreman Information" :min-width="180">
          <template #default="scope">
            <div class="text-sm flex flex-col gap-1">
              <template v-if="scope.row.foreman_id != null || scope.row.foreman_name">
                <div v-if="scope.row.foreman_id != null" class="flex gap-2 items-baseline">
                  <span class="w-10 shrink-0 text-text-tertiary text-[0.85em]">ID</span>
                  <span>{{ scope.row.foreman_id }}</span>
                </div>
                <div v-if="scope.row.foreman_name" class="flex gap-2 items-baseline">
                  <span class="w-10 shrink-0 text-text-tertiary text-[0.85em]">Name</span>
                  <span>{{ scope.row.foreman_name }}</span>
                </div>
              </template>
              <span v-else class="text-text-tertiary">–</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="updated_at" label="Last Updated" :min-width="196" :formatter="dateTimeFormatter" />
      </el-table>
      <el-pagination
        v-if="data"
        v-model:current-page="filters.page"
        v-model:page-size="filters.per_page"
        class="flex justify-end"
        background
        :layout="DEFAULT_PAGINATION_LAYOUT"
        :page-sizes="DEFAULT_PAGE_SIZES"
        :page-count="data.total_pages"
        :total="data.total_records"
        hide-on-single-page
      />
    </template>
  </ViewLayout>
</template>
